import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES } from "@/data/constants";

const INITIAL = {
  full_name: "", company_name: "", country: "", email: "", phone: "", website: "",
  commodity_category: "", product: "", processing_type: "",
  quantity: "", unit: "MT",
  packaging_preference: "", specifications: "",
  destination_country: "", destination_port: "", incoterm: "",
  preferred_shipment_date: "", delivery_timeline: "",
  target_price: "", payment_terms: "", private_label: "",
  required_certifications: "", message: "",
};

const INCOTERMS = ["", "FOB", "CIF", "CFR", "Ex-Works", "DAP", "DDP"];

export default function QuotationPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [products, setProducts] = useState([]);
  const [wa, setWa] = useState("919848490057");
  const [lastSubmission, setLastSubmission] = useState(null);
  const formRef = useRef(null);

  useEffect(() => { document.title = "Request a Quotation — Aaha Kanakam Agri Traders"; }, []);

  useEffect(() => {
    api.get("/site-config").then(({ data }) => setWa(data.business_whatsapp || "919848490057")).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  // When products load or the URL changes, pre-fill product if ?product=slug is present
  useEffect(() => {
    const slug = params.get("product");
    if (!slug || products.length === 0) return;
    const found = products.find((p) => p.slug === slug);
    if (found) {
      setForm((f) => ({
        ...f,
        product: found.name,
        commodity_category: found.category,
        processing_type: (found.processing_types && found.processing_types[0]) || f.processing_type,
      }));
      // Ensure the form is visible when landing here from a product page
      setTimeout(() => {
        if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [params, products]);

  // Options for the Product dropdown (grouped by category)
  const groupedProducts = useMemo(() => {
    const g = {};
    products.forEach((p) => {
      if (!g[p.category]) g[p.category] = [];
      g[p.category].push(p);
    });
    return g;
  }, [products]);

  // Processing options for the chosen product (if any)
  const processingOptions = useMemo(() => {
    const p = products.find((pr) => pr.name === form.product);
    return p?.processing_types || [];
  }, [products, form.product]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.company_name.trim()) e.company_name = "Required";
    if (!form.country.trim()) e.country = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.product.trim()) e.product = "Please select a product";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onProductChange = (name) => {
    const p = products.find((pr) => pr.name === name);
    setForm((f) => ({
      ...f,
      product: name,
      commodity_category: p ? p.category : f.commodity_category,
      processing_type: p && p.processing_types && p.processing_types.length === 1 ? p.processing_types[0] : f.processing_type,
    }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you. Your enquiry has been received. Our team will review your requirement and get back to you shortly.");
      setLastSubmission({ ...form });
      setSubmitted(true);
      setForm(INITIAL);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappForwardHref = () => {
    if (!lastSubmission) return `https://wa.me/${wa}`;
    const s = lastSubmission;
    const lines = [
      "NEW WEBSITE ENQUIRY",
      "",
      `Name: ${s.full_name}`,
      `Company: ${s.company_name}`,
      `Country: ${s.country}`,
      `Email: ${s.email}`,
      `Phone / WhatsApp: ${s.phone}`,
      s.website && `Website: ${s.website}`,
      "",
      `Product: ${s.product}`,
      s.processing_type && `Processing: ${s.processing_type}`,
      s.commodity_category && `Category: ${s.commodity_category}`,
      s.quantity && `Quantity: ${s.quantity} ${s.unit}`,
      s.packaging_preference && `Packaging: ${s.packaging_preference}`,
      s.destination_country && `Destination Country: ${s.destination_country}`,
      s.destination_port && `Destination Port / City: ${s.destination_port}`,
      s.incoterm && `Incoterm: ${s.incoterm}`,
      s.delivery_timeline && `Delivery Timeline: ${s.delivery_timeline}`,
      s.preferred_shipment_date && `Preferred Shipment: ${s.preferred_shipment_date}`,
      s.target_price && `Target Price: ${s.target_price}`,
      s.payment_terms && `Payment Terms: ${s.payment_terms}`,
      s.private_label && `Private Label: ${s.private_label}`,
      s.required_certifications && `Certifications: ${s.required_certifications}`,
      s.specifications && `Specifications: ${s.specifications}`,
      s.message && `Message: ${s.message}`,
      "",
      "Source: Aaha Kanakam Website",
    ].filter(Boolean).join("\n");
    return `https://wa.me/${wa}?text=${encodeURIComponent(lines)}`;
  };

  return (
    <div data-testid="quotation-page">
      <section className="bg-forest text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="eyebrow text-gold mb-3">Request a Quotation</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">Tell us what you need. We will explore the right sourcing option.</h1>
          <p className="mt-5 max-w-2xl text-white/70">Share your product requirements, quantity and destination. Fields marked * are required.</p>
        </div>
      </section>

      <section ref={formRef} className="py-14 md:py-16 bg-ivory scroll-mt-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          {submitted && (
            <div data-testid="quote-success" className="mb-8 p-6 bg-emerald-50 border border-emerald-200 fade-in-up">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-900 mb-1">Enquiry received</div>
                  <p className="text-sm text-emerald-800 leading-relaxed">Thank you. Our team will review your requirement and get back to you shortly by email or WhatsApp.</p>
                  {lastSubmission && (
                    <a
                      href={whatsappForwardHref()}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="post-submit-whatsapp"
                      className="mt-4 inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold hover:brightness-95 transition"
                    >
                      <MessageCircle size={16} /> Also send this enquiry via WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="bg-white border border-borderSubtle p-6 md:p-10 space-y-8">
            <Fieldset title="Buyer Details">
              <Grid>
                <Field label="Full Name *" error={errors.full_name}>
                  <input data-testid="quote-input-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Company Name *" error={errors.company_name}>
                  <input data-testid="quote-input-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Country *" error={errors.country}>
                  <input data-testid="quote-input-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} placeholder="e.g. United Arab Emirates" />
                </Field>
                <Field label="Business Email *" error={errors.email}>
                  <input type="email" data-testid="quote-input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Phone / WhatsApp *" error={errors.phone}>
                  <input data-testid="quote-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+91 9848490057" />
                </Field>
                <Field label="Website (optional)">
                  <input data-testid="quote-input-website" value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} />
                </Field>
              </Grid>
            </Fieldset>

            <Fieldset title="Requirement">
              <Grid>
                <Field label="Product / Variety *" full error={errors.product}>
                  <select data-testid="quote-input-product" value={form.product} onChange={(e) => onProductChange(e.target.value)} className={inputCls}>
                    <option value="">Select a product</option>
                    {Object.entries(groupedProducts).map(([cat, list]) => (
                      <optgroup key={cat} label={cat}>
                        {list.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                {processingOptions.length > 0 && (
                  <Field label="Processing Type" full>
                    <div className="flex flex-wrap gap-2">
                      {processingOptions.map((opt) => (
                        <button
                          type="button"
                          key={opt}
                          data-testid={`quote-processing-${opt.toLowerCase().replace(/\W+/g, "-")}`}
                          onClick={() => set("processing_type", opt)}
                          className={`px-3.5 py-1.5 text-xs uppercase tracking-widest border ${form.processing_type === opt ? "bg-forest text-white border-forest" : "bg-white border-borderSubtle hover:border-forest"}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </Field>
                )}
                <Field label="Commodity Category">
                  <select data-testid="quote-input-category" value={form.commodity_category} onChange={(e) => set("commodity_category", e.target.value)} className={inputCls}>
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Required Quantity">
                  <input data-testid="quote-input-quantity" type="number" min="0" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className={inputCls} placeholder="e.g. 25" />
                </Field>
                <Field label="Unit of Measurement">
                  <select data-testid="quote-input-unit" value={form.unit} onChange={(e) => set("unit", e.target.value)} className={inputCls}>
                    <option>MT</option><option>KG</option><option>Containers (FCL)</option>
                  </select>
                </Field>
                <Field label="Packaging Preference">
                  <input data-testid="quote-input-packaging" value={form.packaging_preference} onChange={(e) => set("packaging_preference", e.target.value)} className={inputCls} placeholder="e.g. 25 kg PP bags, private label" />
                </Field>
                <Field label="Required Specifications" full>
                  <textarea data-testid="quote-input-specs" rows={3} value={form.specifications} onChange={(e) => set("specifications", e.target.value)} className={inputCls} placeholder="Moisture, grain length, purity, crop year…" />
                </Field>
              </Grid>
            </Fieldset>

            <Fieldset title="Shipment">
              <Grid>
                <Field label="Destination Country">
                  <input data-testid="quote-input-dest-country" value={form.destination_country} onChange={(e) => set("destination_country", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Destination Port / City">
                  <input data-testid="quote-input-dest-port" value={form.destination_port} onChange={(e) => set("destination_port", e.target.value)} className={inputCls} placeholder="e.g. Jebel Ali, UAE" />
                </Field>
                <Field label="Incoterm">
                  <select data-testid="quote-input-incoterm" value={form.incoterm} onChange={(e) => set("incoterm", e.target.value)} className={inputCls}>
                    {INCOTERMS.map((i) => <option key={i || "none"} value={i}>{i || "Select"}</option>)}
                  </select>
                </Field>
                <Field label="Required Delivery Timeline">
                  <input data-testid="quote-input-timeline" value={form.delivery_timeline} onChange={(e) => set("delivery_timeline", e.target.value)} className={inputCls} placeholder="e.g. November 2026" />
                </Field>
                <Field label="Preferred Shipment Date">
                  <input data-testid="quote-input-date" type="date" value={form.preferred_shipment_date} onChange={(e) => set("preferred_shipment_date", e.target.value)} className={inputCls} />
                </Field>
                <Field label="Target Price (optional)">
                  <input data-testid="quote-input-price" value={form.target_price} onChange={(e) => set("target_price", e.target.value)} className={inputCls} placeholder="e.g. USD 650 / MT CIF" />
                </Field>
              </Grid>
            </Fieldset>

            <Fieldset title="Optional Details">
              <Grid>
                <Field label="Preferred Payment Terms">
                  <input data-testid="quote-input-payment" value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} className={inputCls} placeholder="e.g. LC at sight, 30% advance" />
                </Field>
                <Field label="Private Label Requirement">
                  <input data-testid="quote-input-private-label" value={form.private_label} onChange={(e) => set("private_label", e.target.value)} className={inputCls} placeholder="Yes / No / Brand details" />
                </Field>
                <Field label="Required Certifications" full>
                  <input data-testid="quote-input-certs" value={form.required_certifications} onChange={(e) => set("required_certifications", e.target.value)} className={inputCls} placeholder="e.g. Phytosanitary, Fumigation, COA, Halal" />
                </Field>
                <Field label="Message / Additional Notes" full>
                  <textarea data-testid="quote-input-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} />
                </Field>
              </Grid>
            </Fieldset>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="text-xs text-slateneutral">By submitting, you agree that we may contact you regarding your enquiry.</p>
              <button data-testid="quote-form-submit-button" type="submit" disabled={submitting} className="btn-gold px-8 py-3.5 text-sm font-semibold tracking-wide inline-flex items-center gap-2 disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit Enquiry"} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full px-4 py-3 text-sm bg-white border border-borderSubtle focus:outline-none focus:border-forest transition";

function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-5">{children}</div>; }

function Fieldset({ title, children }) {
  return (
    <div>
      <div className="eyebrow mb-5 pb-3 border-b border-borderSubtle">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, error, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-widest text-charcoal font-semibold block mb-2">{label}</span>
      {children}
      {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
    </label>
  );
}
